import { Component, EventEmitter, Input, Output, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CapicheMaterialModule } from 'capiche-material';
import { StripeApiService } from 'capiche-subscription';
import { CapicheLoggerService } from 'capiche-logger';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ColorPaletteComponent } from '../color-palette/color-palette.component';
import { FontPreferenceService, FontChoice } from '../../services/font-preference/font-preference.service';
import { VoicePreferenceService } from 'capiche-text-processor';
import { CAPICHE_COMMUNITY_URL, CAPICHE_COMMUNITY_BLURB } from '../../constants';

@Component({
  selector: 'header-contact-support-dialog',
  imports: [CommonModule, CapicheMaterialModule],
  template: `
    <h1 mat-dialog-title>Subscription Error</h1>
    <div mat-dialog-content>
      <p>We are very sorry, there was an error processing your subscription.</p>
      <p>Please use the green button at the bottom-right to contact support for assistance.</p>
      <p>We'll sort it out for you and be in touch. Thank you for your patience.</p>
    </div>
    <div mat-dialog-actions>
      <button mat-button mat-dialog-close>Close</button>
    </div>
  `,
})
export class headerContactSupportDialog {}

@Component({
  selector: 'loading-dialog',
  imports: [CommonModule, CapicheMaterialModule],
  template: `
    <div
      class="loading-container"
      style="
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 20px;
        text-align: center;
        color: #555;"
    >
      <mat-spinner style="margin-bottom: 16px;"></mat-spinner>
      <h2 class="mat-h2">We're working on it.</h2>
      <p class="mat-body-1">Just a moment while we get things ready for you.</p>
    </div>
  `,
})
export class LoadingDialog {}

@Component({
  selector: 'app-header',
  imports: [CommonModule, CapicheMaterialModule, ColorPaletteComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
})
export class HeaderComponent implements OnChanges {
  private logger = new CapicheLoggerService('header.component:', false);
  /** Legacy input — kept for apps that haven't migrated to isLoggedIn/isB2C yet
   *  (e.g. the chrome extension). When those apps set this, both `effectiveLoggedIn`
   *  and `effectiveB2C` fall back to it, reproducing the old all-or-nothing behaviour. */
  @Input() showProfileIcon: boolean = false;
  /** CSW-1379 follow-up: true for ANY authenticated user (B2C, teacher, student).
   *  Drives voice/colour — those are useful regardless of account type. Pass `null`
   *  (the default) to fall back to `showProfileIcon` for un-migrated consumers. */
  @Input() isLoggedIn: boolean | null = null;
  /** CSW-1379 follow-up: true only when logged in AND neither a teacher nor a student.
   *  Drives the "account" icon + its menu (Manage account, Teacher Portal link, Terms
   *  of use, Privacy policy) — none of that applies to teachers (own portal) or students
   *  (institutionally managed). Pass `null` (the default) to fall back to `showProfileIcon`. */
  @Input() isB2C: boolean | null = null;
  //ToDo: fix this typing issue properly and replace any with the correct type
  @Input() env: any;
  /** When true, shows a "Teacher Portal" link in the profile menu. */
  @Input() isTeacher: boolean = false;
  /** URL of the teacher portal (passed from environment). */
  @Input() teacherPortalUrl: string = '';
  /** CSW-1379 follow-up: true when this header is rendered inside the teacher-portal's
   *  editor iframe (/embed/editor). The iframe is chrome-less by design — the host
   *  dashboard already provides its own sign-out — so showing a second, independent
   *  logout button inside the embedded editor makes no sense and is actively confusing
   *  (it logs the user out of the underlying web-app session, not the dashboard). */
  @Input() hideLogout: boolean = false;
  /** CSW-1379 follow-up: same iframe case as hideLogout — the teacher-portal dashboard
   *  hosting /embed/editor already shows the Capiche brand/logo in its own chrome, so
   *  a second logo inside the embedded editor's header is redundant clutter. */
  @Input() hideLogo: boolean = false;
  /** CSW-1577: the colour picker repaints a ProseMirror `#editor` element
   *  (see ProseMirrorEditorComponent) — meaningless in a host with no editor,
   *  e.g. the chrome extension's side panel (a read-only suggestions list).
   *  Defaults to false (shown) so every existing consumer is unaffected. */
  @Input() hideColorPalette: boolean = false;
  /**
   * CSW-1671: show a permanent "Community" item in the account menu that opens the
   * Capiche User Community Facebook group. The account menu is already B2C-only, so
   * the web-app additionally passes `!isEmbedded` to keep it out of the teacher-portal
   * editor iframe (the portal surfaces the same group through its own guide, CSW-1670).
   */
  @Input() showCommunityLink: boolean = false;
  /**
   * CSW-1671: when this flips true the header runs the one-time community nudge — it
   * jiggles the account icon, opens the account menu, and shows a small "join?" card
   * at the top of the menu panel. The reveal doubles as the tutorial for where the
   * Community link lives from then on. The host decides when to set this (once ever,
   * when the editor is idle) and persists that it happened off `communityPromptShown`.
   */
  @Input() promptCommunity: boolean = false;
  @Output() logoutEmitter = new EventEmitter();
  /** Group was opened — from the menu item or the nudge card's "Join now". */
  @Output() communityOpened = new EventEmitter<void>();
  /** The nudge was shown (menu auto-opened). Host persists "don't prompt again" here. */
  @Output() communityPromptShown = new EventEmitter<void>();
  /** The nudge card's "No thanks" was clicked. */
  @Output() communityDeclined = new EventEmitter<void>();

  @ViewChild('profileTrigger') private profileTrigger?: MatMenuTrigger;

  readonly communityUrl = CAPICHE_COMMUNITY_URL;
  readonly communityBlurb = CAPICHE_COMMUNITY_BLURB;
  /** Drives the one-shot CSS jiggle on the account icon. */
  jiggleAccount = false;
  private communityCardDismissed = false;

  /** The nudge card shows only while prompting and before it has been answered. */
  get showCommunityCard(): boolean {
    return this.promptCommunity && !this.communityCardDismissed;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const p = changes['promptCommunity'];
    if (p && p.currentValue && !p.previousValue) {
      this.runCommunityPrompt();
    }
  }

  private runCommunityPrompt(): void {
    // Guard: the account menu (and its trigger) only exist for B2C users.
    if (!this.effectiveB2C || !this.showCommunityLink) return;
    this.communityCardDismissed = false;
    this.jiggleAccount = true;
    setTimeout(() => (this.jiggleAccount = false), 600);
    // Let the jiggle register before the menu covers the icon.
    setTimeout(() => {
      this.profileTrigger?.openMenu();
      this.communityPromptShown.emit();
    }, 450);
  }

  onCommunityOpen(): void {
    window.open(this.communityUrl, '_blank', 'noopener');
    this.communityCardDismissed = true;
    this.communityOpened.emit();
    this.profileTrigger?.closeMenu();
  }

  onCommunityDecline(): void {
    // Collapse the card but leave the menu open — the plain "Community" item is now
    // visible in its place, which shows the user where to find it later.
    this.communityCardDismissed = true;
    this.communityDeclined.emit();
  }

  get effectiveLoggedIn(): boolean {
    return this.isLoggedIn ?? this.showProfileIcon;
  }

  get effectiveB2C(): boolean {
    return this.isB2C ?? this.showProfileIcon;
  }

  selectedFont: FontChoice = 'default';
  selectedVoiceName: string | null = null;
  voiceList: SpeechSynthesisVoice[] = [];

  fontOptions: { value: FontChoice; label: string; fontFamily: string }[] = [
    { value: 'default', label: 'Default Font', fontFamily: "'Roboto', Arial, sans-serif" },
    { value: 'openDyslexic', label: 'OpenDyslexic', fontFamily: "'OpenDyslexic', Arial, sans-serif" },
    { value: 'courier', label: 'Courier', fontFamily: "'Courier New', Courier, monospace" },
  ];

  constructor(
    private stripeApi: StripeApiService,
    private dialog: MatDialog,
    private fontPreferenceService: FontPreferenceService,
    private voicePreferenceService: VoicePreferenceService,
  ) {}

  ngOnInit() {
    this.fontPreferenceService.selectedFont$.subscribe((font) => {
      this.selectedFont = font;
    });
    this.voicePreferenceService.voiceList$.subscribe((list: SpeechSynthesisVoice[]) => {
      this.voiceList = list;
    });
    this.voicePreferenceService.selectedVoice$.subscribe((voice: SpeechSynthesisVoice | null) => {
      this.selectedVoiceName = voice?.name ?? null;
    });
  }

  get selectedFontFamily(): string {
    return this.fontOptions.find((o) => o.value === this.selectedFont)?.fontFamily ?? '';
  }

  onFontChange(font: FontChoice) {
    this.fontPreferenceService.setFont(font);
  }

  onVoiceChange(voiceName: string) {
    this.voicePreferenceService.setVoice(voiceName);
  }

  async onManageAccount() {
    const loadingDialogRef = this.dialog.open(LoadingDialog, {
      disableClose: true,
    });
    const portalUrl: string = await this.stripeApi.getCustomerPortalUrl();
    this.logger.log('onManageAccount: portalUrl: ', portalUrl);
    if (portalUrl) {
      window.location.href = portalUrl;
    } else {
      this.logger.log('No URL returned from getCheckoutUrl');
      this.logger.log('Opening contact support dialog');
      const dialogRef = this.dialog.open(headerContactSupportDialog);

      dialogRef.afterClosed().subscribe((result) => {
        this.logger.log('The dialog was closed');
      });
    }
    loadingDialogRef.close();
  }

  onTermsOfUse() {
    const termsOfUseUrl = this.env.capicheAppTermsOfUseUrl;
    window.open(termsOfUseUrl, '_blank');
  }

  onPrivacyPolicy() {
    const termsOfUseUrl = this.env.privacyPolicyUrl;
    window.open(termsOfUseUrl, '_blank');
  }

  onLogout() {
    this.logoutEmitter.emit();
  }
}
